import type { CreateTargetInput, Target } from '@taler/api-client'
import { targetKeys } from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { problemDetail } from '../internal/problem.ts'

export interface UseTargetsOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseTargets {
  readonly targets: ComputedRef<readonly Target[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isCreating: ComputedRef<boolean>
  readonly hasCreateError: ComputedRef<boolean>
  readonly createErrorDetail: ComputedRef<string | undefined>
  readonly deletingId: ComputedRef<string | undefined>
  readonly hasDeleteError: ComputedRef<boolean>
  readonly deleteErrorDetail: ComputedRef<string | undefined>
  create(input: CreateTargetInput): Promise<Target>
  remove(targetId: string): Promise<void>
  retry(): void
}

export function useTargets(options: UseTargetsOptions): UseTargets {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const targetsQuery = useQuery({
    queryKey: computed(() => targetKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.targets.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  function invalidate(): void {
    for (const key of targetKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateTargetInput) => api.targets.create(options.ledgerId()!, input),
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (targetId: string) => api.targets.delete(options.ledgerId()!, targetId),
    onSuccess: invalidate,
  })

  return {
    targets: computed(() => targetsQuery.data.value ?? []),
    isLoading: computed(() => active.value && targetsQuery.isPending.value),
    error: computed(() => targetsQuery.error.value),
    retrying: retrying(targetsQuery),
    isCreating: computed(() => createMutation.isPending.value),
    hasCreateError: computed(() => createMutation.error.value !== null),
    createErrorDetail: computed(() => problemDetail(createMutation.error.value)),
    deletingId: computed(() =>
      deleteMutation.isPending.value ? (deleteMutation.variables.value as string | undefined) : undefined,
    ),
    hasDeleteError: computed(() => deleteMutation.error.value !== null),
    deleteErrorDetail: computed(() => problemDetail(deleteMutation.error.value)),
    create: (input) => createMutation.mutateAsync(input),
    remove: (targetId) => deleteMutation.mutateAsync(targetId),
    retry: () => {
      void targetsQuery.refetch()
    },
  }
}
